import React, { useState, useEffect, useRef } from 'react';
import { Minus, Plus, X, Phone } from 'lucide-react';
import { useCart, OrderData } from '../context/CartContext';
import { useLanguage } from '../context/LanguageContext';
import { useLocation } from 'react-router-dom';
import ReviewPrompt from './ReviewPrompt';

// Module-level guard: two Cart instances can mount on some routes, so a
// per-instance window listener would double up. Only one alert per burst.
let lastErrorAlertAt = 0;
const ERROR_ALERT_COOLDOWN_MS = 3000;

const Cart: React.FC = () => {
  const { 
    isOpen, 
    toggleCart, 
    openCart,
    items, 
    removeFromCart, 
    updateQuantity,
    updateSpecialInstructions, 
    total, 
    deliveryType,
    tableNumber,
    tableName,
    submitOrder,
    toggleItemInstructions,
    setTableInfo,
    pendingQrOrder,
    consumePendingQrOrder
  } = useCart();
  
  const { t, language } = useLanguage();
  const location = useLocation();
  const [currentStep, setCurrentStep] = useState<'cart' | 'customerInfo'>('cart');
  const [customerInfo, setCustomerInfo] = useState({ 
    name: '', 
    address: '', 
    phone: '' 
  });
  const [showReviewPrompt, setShowReviewPrompt] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const isSubmittingRef = useRef(false);
  const autoOpenedRef = useRef(false);

  useEffect(() => {
    isSubmittingRef.current = isSubmitting;
  }, [isSubmitting]);

  useEffect(() => {
    const handleError = () => {
      // Only surface unexpected errors while an order is being submitted,
      // and only one alert per burst (two Cart instances can mount).
      if (!isSubmittingRef.current) return;
      const now = Date.now();
      if (now - lastErrorAlertAt < ERROR_ALERT_COOLDOWN_MS) return;
      lastErrorAlertAt = now;
      alert(t('cart.errorSubmittingOrder'));
    };

    window.addEventListener('error', handleError);
    return () => window.removeEventListener('error', handleError);
  }, [t]);

  useEffect(() => {
    if (autoOpenedRef.current) return;

    // QR parameters live at '/', but DeliveryOption replaces the navigation
    // with navigate('/menu', { replace: true }) which strips the search
    // string before Cart mounts. CartProvider therefore snapshots them at
    // boot (pendingQrOrder); location.search is used as a fallback in case
    // the params survive the navigation.
    let tableNum: string | null = null;
    let tableNameParam: string | null = null;
    let mode: string | null = null;

    if (pendingQrOrder) {
      tableNum = pendingQrOrder.tableNumber;
      tableNameParam = pendingQrOrder.tableName;
      mode = pendingQrOrder.mode;
    } else {
      const params = new URLSearchParams(location.search);
      tableNum = params.get('table');
      tableNameParam = params.get('name');
      mode = params.get('mode');
    }

    if (tableNum && tableNameParam && mode === 'order') {
      autoOpenedRef.current = true;
      setTableInfo(tableNum, tableNameParam);
      openCart();
      consumePendingQrOrder();
    }
  }, [pendingQrOrder, location.search, setTableInfo, openCart, consumePendingQrOrder]);

  const placeOrder = async (orderData: OrderData) => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    setSubmitError(null);
    try {
      await submitOrder(orderData);
      setShowReviewPrompt(true);
    } catch (error) {
      console.error('Order submission failed:', error);
      setSubmitError(t('cart.errorSubmittingOrder'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleContinue = async () => {
    if (isSubmitting || items.length === 0) return;

    const effectiveType = deliveryType ??
      (localStorage.getItem('deliveryOption') as 'surplace' | 'livraison' | null);

    if (effectiveType === 'livraison') {
      setCurrentStep('customerInfo');
    } else if (effectiveType === 'surplace') {
      const orderData: OrderData = {
        customerInfo: null,
        deliveryType: 'surplace',
        items: items.map(item => ({
          name: item.translations[language].name,
          quantity: item.quantity,
          price: item.price,
          specialInstructions: item.specialInstructions
        })),
        total: total,
        tableInfo: tableNumber && tableName 
          ? {
              tableNumber,
              tableName
            } 
          : null,
        globalSpecialInstructions: ''
      };

      await placeOrder(orderData);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitting || items.length === 0) return;

    const orderData: OrderData = {
      customerInfo: customerInfo,
      deliveryType: 'livraison',
      items: items.map(item => ({
        name: item.translations[language].name,
        quantity: item.quantity,
        price: item.price,
        specialInstructions: item.specialInstructions
      })),
      total: total,
      tableInfo: null,
      globalSpecialInstructions: ''
    };

    await placeOrder(orderData);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4 transition-all">
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="sticky top-0 z-10 flex justify-between items-center p-6 border-b border-gray-100 bg-white/95 backdrop-blur-sm rounded-t-3xl">
          <h2 className="text-2xl font-bold text-gray-800">
            {t(deliveryType === 'surplace' ? 'cart.titlePickup' : 'cart.titleDelivery')}
          </h2>
          <button 
            onClick={toggleCart}
            aria-label="Fermer le panier"
            className="p-2 hover:bg-gray-100 rounded-full transition-all duration-200"
          >
            <X size={24} className="text-gray-500" />
          </button>
        </div>

        {/* Cart Content */}
        <div className="flex-1 overflow-y-auto">
          {currentStep === 'cart' && (
            <div className="p-6 space-y-6">
              {items.length === 0 && (
                <p className="text-center text-gray-500 py-8">
                  {t('cart.emptyCart')}
                </p>
              )}
              {items.map((item) => (
                <div key={item.cartId} className="bg-gray-50 rounded-2xl p-4 hover:shadow-md transition-all duration-200">
                  <div className="flex justify-between items-start gap-4">
                    <div className="flex-1">
                      <h3 className="font-semibold text-lg text-gray-800">
                        {item.translations[language].name}
                      </h3>
                      <p className="text-gray-600 text-sm mt-1">
                        {item.price * item.quantity} DH
                      </p>
                      
                      {/* Special Instructions */}
                      {item.hasSpecialInstructions && (
                        <div className="mt-2">
                          <label
                            htmlFor={`special-instructions-${item.cartId}`}
                            className="block text-sm font-medium text-gray-700 mb-1"
                          >
                            {t('cart.specialInstructions')}
                          </label>
                          <textarea
                            id={`special-instructions-${item.cartId}`}
                            className="w-full p-2 border rounded-lg text-sm"
                            placeholder={t('cart.itemInstructionsPlaceholder')}
                            value={item.specialInstructions || ''}
                            onChange={(e) => updateSpecialInstructions(item.id, e.target.value)}
                          />
                        </div>
                      )}
                    </div>
                    
                    {/* Quantity Controls */}
                    <div className="flex items-center gap-3 bg-white rounded-xl p-2 shadow-sm">
                      <button
                        onClick={() => updateQuantity(item.id, item.quantity - 1)}
                        aria-label="Diminuer la quantité"
                        className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
                      >
                        <Minus size={16} />
                      </button>
                      <span className="w-8 text-center font-medium">{item.quantity}</span>
                      <button
                        onClick={() => updateQuantity(item.id, item.quantity + 1)}
                        aria-label="Augmenter la quantité"
                        className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
                      >
                        <Plus size={16} />
                      </button>
                    </div>
                    
                    {/* Remove Item */}
                    <button
                      onClick={() => removeFromCart(item.id)}
                      aria-label={`Retirer ${item.translations[language].name}`}
                      className="p-2 hover:bg-red-50 text-red-500 rounded-lg transition-colors"
                    >
                      <X size={20} />
                    </button>
                  </div>
                  
                  {/* Toggle Special Instructions */}
                  <button
                    onClick={() => toggleItemInstructions(item.id)}
                    className="mt-2 text-sm text-primary hover:text-primary-dark transition-colors"
                  >
                    {item.hasSpecialInstructions ? t('cart.hideInstructions') : t('cart.addInstructions')}
                  </button>
                </div>
              ))}

              {submitError && (
                <div role="alert" className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-xl">
                  {submitError}
                </div>
              )}
            </div>
          )}

          {/* Customer Info Form */}
          {currentStep === 'customerInfo' && (
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label
                  htmlFor="cart-customer-name"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  {t('cart.name')}
                </label>
                <input
                  id="cart-customer-name"
                  name="name"
                  type="text"
                  placeholder={t('cart.name')}
                  required
                  autoComplete="name"
                  className="w-full p-4 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary bg-white"
                  value={customerInfo.name}
                  onChange={(e) => setCustomerInfo({ ...customerInfo, name: e.target.value })}
                />
              </div>

              <div>
                <label
                  htmlFor="cart-customer-phone"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  {t('cart.phone')}
                </label>
                <div className="relative">
                  <Phone className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                  <input
                    id="cart-customer-phone"
                    name="phone"
                    type="tel"
                    placeholder={t('cart.phone')}
                    required
                    autoComplete="tel"
                    className="w-full p-4 pl-12 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary bg-white"
                    value={customerInfo.phone}
                    onChange={(e) => setCustomerInfo({ ...customerInfo, phone: e.target.value })}
                  />
                </div>
              </div>

              <div>
                <label
                  htmlFor="cart-customer-address"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  {t('cart.address')}
                </label>
                <input
                  id="cart-customer-address"
                  name="address"
                  type="text"
                  placeholder={t('cart.address')}
                  required
                  autoComplete="street-address"
                  className="w-full p-4 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary bg-white"
                  value={customerInfo.address}
                  onChange={(e) => setCustomerInfo({ ...customerInfo, address: e.target.value })}
                />
              </div>

              {submitError && (
                <div role="alert" className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-xl">
                  {submitError}
                </div>
              )}

              <button
                type="submit"
                disabled={isSubmitting || items.length === 0}
                aria-busy={isSubmitting}
                className="w-full bg-primary text-white py-4 rounded-xl font-semibold hover:bg-primary-dark 
                         transition-all duration-200 flex items-center justify-center gap-2
                         disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {t('cart.submit')}
              </button>
            </form>
          )}
        </div>

        {/* Footer */}
        {currentStep === 'cart' && (
          <div className="sticky bottom-0 bg-white/95 backdrop-blur-sm border-t border-gray-100 px-6 pt-6 pb-[calc(1.5rem+env(safe-area-inset-bottom))] rounded-b-3xl">
            <div className="flex justify-between items-center mb-4">
              <span className="text-lg font-medium text-gray-600">{t('cart.totalToPay')}</span>
              <span className="text-2xl font-bold text-gray-800">{total} DH</span>
            </div>
            <button
              onClick={handleContinue}
              disabled={isSubmitting || items.length === 0}
              aria-busy={isSubmitting}
              className="w-full bg-primary text-white py-4 rounded-xl font-semibold hover:bg-primary-dark 
                       transition-all duration-200 flex items-center justify-center gap-2
                       disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {t('cart.continue')}
            </button>
          </div>
        )}
      </div>

      {showReviewPrompt && (
        <ReviewPrompt onClose={() => setShowReviewPrompt(false)} />
      )}
    </div>
  );
};

export default Cart;
